import { Controller } from "@hotwired/stimulus"

// Switches between tab panels, remembering the selection per page in sessionStorage so a
// save or a reload lands the user back where they were.
// Connects to data-controller="erp--tabs"
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { active: String }

  static ACTIVE_CLASSES = ["bg-black", "text-white"]
  static INACTIVE_CLASSES = ["bg-erp-card", "text-black", "hover:bg-black/5"]

  connect() {
    this.#connectAria()
    this.select({ currentTarget: null, restored: this.#restore() })
  }

  select(event) {
    const name = event?.currentTarget?.dataset?.tabName || event?.restored || this.activeValue
    if (!name) return

    this.activeValue = name
    this.#persist(name)

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.tabName === name
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.tabIndex = active ? 0 : -1
      tab.classList.toggle("border-b-0", active)
      this.constructor.ACTIVE_CLASSES.forEach((c) => tab.classList.toggle(c, active))
      this.constructor.INACTIVE_CLASSES.forEach((c) => tab.classList.toggle(c, !active))
    })

    this.panelTargets.forEach((panel) => {
      const active = panel.dataset.tabName === name
      panel.classList.toggle("hidden", !active)
      panel.toggleAttribute("hidden", !active)
    })

    this.dispatch("changed", { detail: { name } })
  }

  navigate(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return

    event.preventDefault()
    const current = this.tabTargets.indexOf(event.currentTarget)
    const next = event.key === "Home" ? 0
      : event.key === "End" ? this.tabTargets.length - 1
        : (current + (event.key === "ArrowRight" ? 1 : -1) + this.tabTargets.length) % this.tabTargets.length
    const tab = this.tabTargets[next]
    this.select({ currentTarget: tab })
    tab.focus()
  }

  #connectAria() {
    const prefix = this.element.id || `erp-tabs-${Math.random().toString(36).slice(2)}`
    this.element.id = prefix
    this.tabTargets.forEach(tab => {
      const name = tab.dataset.tabName
      const panel = this.panelTargets.find(candidate => candidate.dataset.tabName === name)
      if (!panel) return

      tab.id = `${prefix}-tab-${name}`
      panel.id = `${prefix}-panel-${name}`
      tab.setAttribute("aria-controls", panel.id)
      panel.setAttribute("aria-labelledby", tab.id)
    })
  }

  #storageKey() {
    return `erp-tabs:${window.location.pathname}`
  }

  #restore() {
    try {
      const stored = window.sessionStorage.getItem(this.#storageKey())
      const known = this.tabTargets.some((tab) => tab.dataset.tabName === stored)
      return known ? stored : null
    } catch {
      return null
    }
  }

  #persist(name) {
    try {
      window.sessionStorage.setItem(this.#storageKey(), name)
    } catch {
      // Private browsing / storage disabled — the tab still works, it just does not persist.
    }
  }
}
